import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import Globals, { URLS } from "../../utility/constants/appConstants";
import { useNavigate } from "react-router";
import NavigationUtil from "../../utility/utils/navigationUtil";
import OverlayLoader from "../loader/overlayLoader";
import resetPasswordStyles from "../../styles/resetPassword.module.scss";
import BaseClient from "../../utility/apiClients/base/baseClient";
import ecommEndpoints from "../../utility/features/endPoints/ecommEndpoints";
import { encryptData } from '../../utility/utils/hashing';
import {LoginUserClient} from '../../utility/apiClients/user/loginClient';
import featureControlClient from '../../utility/apiClients/account/featureControlClient';
import { useDispatch, useSelector } from 'react-redux';
import { setLogin, setCustomerInfo, setUserDetails, setInvoiceCustomerInfo } from '../../reduxStore/login/loginSlice';
import {setSelectedServiceAddress} from "../../reduxStore/myAccount/accountSummarySlice";
import { setLeadAddressInCookie } from "../../utility/features/helpers/leadHelper";
import { checkCookie, getCookie } from '../../utility/utils/cookie';
import { IsLoggedIn } from "../../utility/features/helpers/authHelper";
import resetPasswordTokenValidateClient from "../../utility/apiClients/user/resetPasswordTokenValidateClient";

const ResetPassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const labels = Globals.resetpassword; 

  const { post, get } = BaseClient();
  const { userData } = useSelector((state) => state.login);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm();

  const [resetToken, setResetToken] = useState(null);
  const [resetPasswordState, setResetPasswordState] = useState({
    showValidations: false,
    refresh: 0,
    isNewPass: false,
    apiValidationError: "",
    apiExpiredTokenError: false,
    newPasswordSetEmailRequestSentSuccess: false,
    isLoading: false,
    updateLabels: false,
    isLocalStoragePresent: global.window && window.localStorage,
    displayWorkingHours: "",
  });

  const makeLogin = async (data) => {

    setResetPasswordState(prevState => ({ ...prevState, isLoading: true }));
    data.password += `__usertoken${Date.now()}`;
    data.password = encryptData(data.password, "login");
    
    dispatch(setLogin({ ...userData, custEmail: data.username }));

    try {
        const res = await LoginUserClient(data);

        if (res.status !== 200) {
            return false;
        }
        
        localStorage.setItem("cusToken", JSON.stringify(res.data?.LoginInfo?.custoken));
        dispatch(setLogin({ ...userData, isLoggedIn: true }));

        document.cookie = `persistent=${res.data?.LoginInfo?.LoginFirstName}; path=/; SameSite=None; Secure; domain=.trugreen.com; max-age=1200`;
        
        const customerNums = [];
        res.data.Customers.forEach(c => customerNums.push(c.CustomerNumber));
        localStorage.setItem("customerNumbers", JSON.stringify(customerNums));
        const zipCodes = [];
        const Customers_IB_Statuses = [];
        res?.data?.Customers?.forEach(c => zipCodes.push(c.ServiceAddresses?.[0]?.postalCode));
        res?.data?.Customers?.forEach(c => Customers_IB_Statuses.push({[c.CustomerNumber] : `${c.IBStatus === "ACTIVE" ? true : false}`}));

        const serviceAddressesWithCustomerNumbers = [];
        res.data.Customers.forEach(c => {
            if(c.ServiceAddresses && c.ServiceAddresses.length > 0){
                c.ServiceAddresses.forEach(s => serviceAddressesWithCustomerNumbers.push(s.address+" | "+c.CustomerNumber));
            } 
        });

        dispatch(setCustomerInfo(res.data?.Customers));
        dispatch(setInvoiceCustomerInfo(res.data?.Customers));

        dispatch(setUserDetails({
            cusToken: res.data?.LoginInfo?.custoken,
            firstName: res.data?.LoginInfo?.LoginFirstName,
            lastName: res.data?.LoginInfo?.LoginLastName,
            //is_IB: JSON.stringify(response.data?.Customers?.[0]?.IBStatus),
            Customers_IB_Statuses: JSON.stringify(Customers_IB_Statuses),
            zipCodes: JSON.stringify(zipCodes)
        }));

        /* customer number - service address */
        dispatch(setSelectedServiceAddress(serviceAddressesWithCustomerNumbers[0]));
        localStorage.setItem("serviceAddressesWithCustomerNumbers", JSON.stringify(serviceAddressesWithCustomerNumbers));

        /* ========== save selected service address in leadAddressCookie ========== */
        const serviceAddress = res.data?.Customers[0]?.ServiceAddresses[0];
        const completeAddress = `${serviceAddress && serviceAddress.address ? serviceAddress.address : ""}
                                  ${serviceAddress && serviceAddress.city ? ", " + serviceAddress.city : ""}
                                  ${serviceAddress && serviceAddress.state ? " " + serviceAddress.state : ""}
                                  ${serviceAddress && serviceAddress.zip ? " " + serviceAddress.zip : ""}`;

        setLeadAddressInCookie(completeAddress);

        await featureControl(customerNums);


        // redirect to invocies page
        navigate(NavigationUtil.decodeRedirect("/my-invoices"), { replace: true });
            
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            'event': 'account_action',
            'category': 'Account',
            'action': 'Login Button',
            'label': 'success',
            'customer_id': res.data?.Customers[0]?.CustomerNumber || '',
            'error': {
                'reason': '',
            }
        });

    } catch (error) {
    }
    finally {
        setResetPasswordState((prev) => ({ ...prev, isLoading: false }));
    }
  }

  const featureControl = async (customerNums) => {
      const screenWidth = global.window && window.innerWidth;
      const device = screenWidth > 768 ? "Web" : "Mobile Web";
      try {

          let defaultFlags = [
              "ECOMSSC"
          ];
          const featureRes = await featureControlClient(customerNums, [], device, [], defaultFlags);

          if (featureRes.data.details.customerNumberSummaryFeatureCode.length !== 0 && window.localStorage) {
              const { customerNumberSummaryFeatureCode } = featureRes.data.details;
              let ECOMSSC;
              customerNumberSummaryFeatureCode.forEach(fc => {
                  if (fc.featureCode === "ECOMSSC") {
                      ECOMSSC = fc;
                  }
              });
              /* SSC */
              let SSCFlagObj = {};
              SSCFlagObj["ECOMSSCFlag"] = (ECOMSSC.featureStatus === "N") ? false : true;
              localStorage.setItem("SSCFlagObj", JSON.stringify(SSCFlagObj));
          }
      } catch (error) {      }
  }

  const handleSubmitEmail = async () => {
    setResetPasswordState((prev) => ({
      ...prev,
      newPasswordSetEmailRequestSentSuccess: false,
      isLoading: true,
    }));

    try {
      const response = await post(
        process.env.REACT_APP_ECOMM_URL +
          ecommEndpoints.newPasswordLinkRequest(),
        {
          email: getValues("resetPasswordEmail"),
        },
        {},
        false
      );

      setValue("resetPasswordEmail", "");
      setResetPasswordState((prev) => ({
        ...prev,
        isLoading: false,
        newPasswordSetEmailRequestSentSuccess: true,
      }));
    } catch (error) {
      setResetPasswordState((prev) => ({
        ...prev,
        isLoading: false,
        apiExpiredTokenError: true,
      }));
    }
  };

  const handleResetPasswordSubmit = async () => {
    if (Object.keys(errors).length) {
      setResetPasswordState((prev) => ({
        ...prev,
        showValidations: true,
        refresh: prev.refresh + 1,
      }));
      return;
    }

    setResetPasswordState((prev) => ({ ...prev, isLoading: true }));

    try {
      let cookieGa = checkCookie("_ga") ? getCookie("_ga") : '';
      const newTimeStampValue = new Date().getTime().toString() + "" + Math.floor(Math.random() * 1000000).toString();
      const response = await post(
        process.env.REACT_APP_ECOMM_URL + ecommEndpoints.resetPassword(),
        {
          email: getValues("resetPasswordEmail"),
          newPassword: getValues("newpassword"),
          resetToken,
          SessionID: newTimeStampValue+"-"+cookieGa
        },
        {},
        false
      );

      let buyOnlineVar = NavigationUtil.getUrlParam("new");
      if (buyOnlineVar == null) {
        setResetPasswordState((prev) => ({ ...prev, isLoading: true }));
      }

      let message = "";

      if(response.status === 400){
        message = response.error;
      } else if (response.status === 500){
        message = "Internal Server Error";
      }
      else
        message = response?.data?.message;
      
      if(response?.data && response?.data?.code !== 13002){
          const creds = {
            username: getValues("resetPasswordEmail"),
            password: getValues("newpassword"),
            remember: false
          }

          const loginResponse = await makeLogin(creds);
          if(!loginResponse)
            message = "Login failed";
      }

      setResetPasswordState((prev) => ({
        ...prev,
        isLoading: false,
        apiValidationError: message,
      }));
    } catch (error) {
      if (
        error.response.status === 400 &&
        error.response.data &&
        error.response.data.code != 13001 &&
        error.response.data.code != 13002
      ) {
        setResetPasswordState((prev) => ({
          ...prev,
          apiValidationError: error?.response?.data?.message,
        }));
      } else {
        setResetPasswordState((prev) => ({
          ...prev,
          apiExpiredTokenError: true,
        }));
      }
    } finally {
      setResetPasswordState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const fetchUrlParameters = () => {
    
    const resetToken = NavigationUtil.getUrlParam("resetToken");
    const welcomeToken = NavigationUtil.getUrlParam("welcomeToken");
    let email = NavigationUtil.getUrlParam("email");
 

    if (welcomeToken) {
      decodeWelcomeToken(welcomeToken);
    } else if (IsLoggedIn() && email && resetToken) {
      navigate(URLS.accountSummary, { replace: true });
    } else if (email && resetToken) {
      setValue("resetPasswordEmail", email);
      setResetToken(resetToken);
    } else {
      navigate("/login", { replace: true });
    }
  };

  const decodeWelcomeToken = (token) => {
    if(token.indexOf("$") !== -1){
      let tokenArr = token.split("$");
      setResetToken(tokenArr[0]);
      let email = atob(tokenArr[1]);
     
      let emailRegex = /^[^\s@]+@[^\s@.]+\.[^\s@.]+$/;

      /* check for valid token */
      resetPasswordTokenValidateClient({
        "email" : email,
        "resetToken" : tokenArr[0]
      }).then(
        (response) => {
       
          // /* response True means expired */
          // if(response.data.toString().trim() === "1"){
          //   setResetPasswordState({
          //     ...resetPasswordState,
          //     apiExpiredTokenError: true
          //   });
          // } else if(response.data.toString().trim() === "2"){
          //   /* if password already been created the redirect to forgot password */
          //     navigate(URLS.login, { replace: true });
          //     return false;
          // } 

          // if 0 - it is expired token - navigate to forget password page to send new link
          // if 1 - valid token ;
          // if 2 - password already set - navigate to login page
          if(response.data.toString().trim() === "0"){
              setResetPasswordState({
                ...resetPasswordState,
                apiExpiredTokenError: true,
                newPasswordSetEmailRequestSentSuccess : false
              });
              setValue("resetPasswordEmail", email);
              return false;
          } else if(response.data.toString().trim() === "1"){
              /* valid token - do nothing and allow user to set password */
              setValue("resetPasswordEmail", email);
              return;
          } else if(response.data.toString().trim() === "2"){
              /* if password already been created the redirect to forgot password */
              navigate(URLS.login, { replace: true });
              return false;
          }


          setResetPasswordState({
            ...resetPasswordState,
            isLoading: false
          });

          if(emailRegex.test(email)){
            setResetPasswordState({ ...resetPasswordState, isNewPass: true });
            setValue("resetPasswordEmail", email);
          } else {
            navigate(URLS.login, { replace: true });
          }
        },
        (error) => {
          setResetPasswordState({
            ...resetPasswordState,
            isLoading: false,
            apiExpiredTokenError: true
          });
        }
      );
    } else navigate(URLS.login, { replace: true });
  }

  useEffect(() => {
    fetchUrlParameters();
  }, []);

  return (
    <>
      {resetPasswordState.isLoading && (
        <OverlayLoader isOpen={resetPasswordState.isLoading} />
      )}
      <div className={resetPasswordStyles["reset-password"]}>
        <div className="container">
          

          <div className={resetPasswordStyles["reset-password"]}>
            {/* reset password form */}
            <div className={resetPasswordStyles["container"]}>
              {Object.keys(labels).length !== 0 && (
                <div>
                  {!resetPasswordState.isLoading ? (
                    <>
                      {resetPasswordState.apiExpiredTokenError ? (
                        <>
                          <span className={`d-inline-block ${resetPasswordStyles["gfw-3"]} ${resetPasswordStyles["mb-24"]} text-dark`}>
                            <span style={{color:'red'}}>
                              The link is not valid. To receive a new link <a href={URLS.forgotPassword} className={resetPasswordStyles["forgot-password-link"]}> click here </a> to navigate to Forgot Password page.
                            </span>
                          </span>
                        </>
                      ) : (
                        <>
                          <h1 className={`${resetPasswordStyles["gfs-44"]} ${resetPasswordStyles["gfw-3"]} ${resetPasswordStyles["mb-24"]} ${resetPasswordStyles["text-green-dark"]}`}>
                            {resetPasswordState.isNewPass ? labels.formHeadingNewPass : labels.formHeading}
                          </h1>
                          <span className={`d-inline-block ${resetPasswordStyles["gfw-3"]} ${resetPasswordStyles["mb-24"]} text-dark`}>
                            {resetPasswordState.isNewPass
                              ? labels.formSubHeadingNewPass
                              : labels.formSubHeading}
                          </span>
                        </>
                      )}

                      <div>
                        <div
                          className={resetPasswordStyles["reset-password "]}
                        >
                          <div
                            className={
                              resetPasswordStyles["resetpasswordemail-input"]
                            }
                          >
                            <input
                              type="email"
                              placeholder={labels.emailField}
                              disabled
                              className="form-control"
                              {...register("resetPasswordEmail", {
                                required: "Email is required",
                                pattern: {
                                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                  message: "Enter a valid email address",
                                },
                              })}
                            />

                            {/* Validation Error */}
                            {resetPasswordState.apiValidationError && (
                              <span className={resetPasswordStyles["field-validation-error"]}>
                                {resetPasswordState.apiValidationError}
                              </span>
                            )}
                          </div>

                          <div className="form-group">
                            <input
                              type="password"
                              id="new-password"
                              className="form-control"
                              placeholder={labels.passwordField}
                              {...register("newpassword", {
                                required: "New password is required",
                                minLength: {
                                  value: 8,
                                  message:
                                    "Password must be at least 8 characters",
                                },
                              })}
                            />
                            {errors.newpassword && (
                              <span className="error-text">
                                {errors.newpassword.message}
                              </span>
                            )}
                          </div>

                          {errors.confirmnewpassword && (
                            <span className={resetPasswordStyles["field-validation-error"]}>
                              {errors.confirmnewpassword.message}
                            </span>
                          )}
                          {/* CODE FOR FIELD form  */}
                          <div className="form-group">
                            <input
                              type="password"
                              id="confirm-password"
                              className="form-control"
                              placeholder={labels.confirmPasswordField}
                              {...register("confirmnewpassword", {
                                required: "Please confirm your new password",
                                minLength: {
                                  value: 8,
                                  message:
                                    "Password must be at least 8 characters",
                                },
                                validate: (value) =>
                                  value === getValues("newpassword") ||
                                  "Passwords do not match",
                              })}
                            />
                          </div>
                          {/* end here */}
                          <div className="font-italic gfs-20 gfw-3 glh-1_2 row text-green-dark">
                            <div
                              className={`${resetPasswordStyles["password-rules"]} col-12`}
                              dangerouslySetInnerHTML={{
                                __html: labels.passwordCriteria,
                              }}
                            />
                          </div>
                          <button
                            type="submit"
                            onClick={handleSubmit(handleResetPasswordSubmit)}
                            className={`btn btn-lg ${resetPasswordStyles["submit-btn"]} rounded-0`}
                            disabled={resetPasswordState.apiExpiredTokenError ? true : false}
                          >
                            {" "}
                            {labels.submitButton}{" "}
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="d-inline-block gfs-20 gfw-3 mb-24 text-dark text-center w-100">
                        Validating Token...
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
